import {
  FC,
  RefObject,
} from 'react';
import {
  MultimediaType,
} from 'pages/Dashboard/utils/constants';
import ImageComponent from 'pages/Dashboard/components/Image';
import type {
  FileLocalUrl,
} from 'pages/Dashboard/components/FilePicker';
import {
  jsPDF,
} from 'jspdf';
import type {
  getCurrentImgDataFunction,
} from 'react-filerobot-image-editor';
import {
  EditorImageData,
  GetImgDataRef,
} from 'views/EMR/components/ImageEditorDialog/FilerobotEditor';

export const thumbnailPreviewableExtensions = ['png', 'jpg', 'jpeg', 'pdf'];
export const previewableExtensions = thumbnailPreviewableExtensions.concat(['txt', 'html', 'htm', 'xml']);

export type ThumbnailPreviewableExtension = typeof thumbnailPreviewableExtensions[number];
export type PreviewableExtension = typeof previewableExtensions[number];

export const mimeTypesByMultimediaType: Record<string, string[]> = Object.freeze({
  [MultimediaType.ProfilePicture]: ['png', 'jpg', 'jpeg'],
  [MultimediaType.EOB]: ['png', 'jpg', 'jpeg', 'tiff', 'pdf'],
});

export const extensionToPreviewTag: Record<PreviewableExtension, string | FC<any>> = {
  png: ImageComponent,
  jpg: ImageComponent,
  jpeg: ImageComponent,
  txt: 'div',
  // Desktop PDF preview path; mobile/tablet uses PdfMediaPreview in MediaPreview.
  pdf: 'iframe',
  html: 'iframe',
  htm: 'iframe',
  xml: 'iframe',
};

export function getUrlFromXML(xmlContent: string, xslContent: string): string {
  const xml = new window.DOMParser().parseFromString(xmlContent ?? '', 'application/xml');
  const xslt = new window.DOMParser().parseFromString(xslContent ?? '', 'application/xml');
  const xsltProcessor = new XSLTProcessor();
  xsltProcessor.importStylesheet(xslt);
  const resultDocument = xsltProcessor.transformToFragment(xml, document);
  const serializer = new XMLSerializer();
  const resultString = serializer.serializeToString(resultDocument);

  // Create a Blob from the result string
  const blob = new Blob([resultString], { type: 'text/html' });

  // Create a URL for the Blob
  const url = URL.createObjectURL(blob);

  return url;
}

export const toLocalUrls = (files: File[]): Promise<FileLocalUrl[]> => new Promise((resolve) => {
  const results: FileLocalUrl[] = [];
  if ((files ?? []).length === 0) {
    resolve(results);
  }
  files.forEach((file) => {
    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      results.push({ dataUrl: e.target?.result as string, file });
      if (results.length === files.length) {
        resolve(results);
      }
    };
    reader.readAsDataURL(file);
  });
});

export function dataUrlToFile(dataUrl: string, fileName: string, defaultMime: string = 'image/jpeg'): File {
  const [metadata = '', base64Data = ''] = dataUrl.split(',');
  const mimeMatch = metadata.match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : defaultMime;
  const binaryString = atob(base64Data);
  const { length } = binaryString;
  const bytes = new Uint8Array(length);
  for (let index = 0; index < length; index += 1) {
    bytes[index] = binaryString.charCodeAt(index);
  }
  return new File([bytes], fileName, { type: mime });
}

const fitImageOnPage = (doc: jsPDF, imgWidth: number, imgHeight: number) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const pageAspect = pageWidth / pageHeight;
  const imgAspect = imgWidth / imgHeight;
  let renderWidth = pageWidth;
  let renderHeight = pageHeight;
  if (imgAspect > pageAspect) {
    renderWidth = pageWidth;
    renderHeight = (pageWidth / imgWidth) * imgHeight;
  } else {
    renderHeight = pageHeight;
    renderWidth = (pageHeight / imgHeight) * imgWidth;
  }
  const x = (pageWidth - renderWidth) / 2;
  const y = (pageHeight - renderHeight) / 2;
  return { x, y, renderWidth, renderHeight };
};

const getImageDimensions = (
  dataUrl: string,
): Promise<{ w: number; h: number }> =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ w: img.width, h: img.height });
    img.src = dataUrl;
  });

export async function generatePdfDataUrlForDoc(images: string[]): Promise<string> {
  // eslint-disable-next-line new-cap
  const pdf = new jsPDF({ unit: 'pt', format: 'a4', compress: true });
  for (let i = 0; i < images.length; i += 1) {
    const dataUrl = images[i];
    // eslint-disable-next-line no-await-in-loop
    const { w, h } = await getImageDimensions(dataUrl);
    const { x, y, renderWidth, renderHeight } = fitImageOnPage(pdf, w, h);
    pdf.addImage(dataUrl, 'PNG', x, y, renderWidth, renderHeight, undefined, 'FAST');
    if (i < images.length - 1) pdf.addPage();
  }
  const dataUrl = pdf.output('dataurlstring');
  // Remove filename parameter from data URL if present
  return dataUrl.replace(/;filename=[^;]+/i, '');
}

export async function imageToPdf(images: string[]): Promise<FileLocalUrl> {
  const dataUrl = await generatePdfDataUrlForDoc(images);
  const fileName = `pdf-${Date.now()}.pdf`;
  return {
    dataUrl,
    file: dataUrlToFile(dataUrl, fileName, 'application/pdf'),
  };
}

// Normalize any URL (including blob:) to a data URL for consistent downstream processing
export async function normalizeToDataUrl(input: string): Promise<string> {
  let result = input ?? '';

  if (result && !result.startsWith('data:')) {
    try {
      const res = await fetch(result);
      const blob = await res.blob();
      result = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.readAsDataURL(blob);
      });
    } catch {
      result = input;
    }
  }

  return result;
}

// ----- Editor interop helpers (Filerobot or similar) -----
function resolveEditorGetFn(
  refOrFn:
    | RefObject<getCurrentImgDataFunction | null>
    | { current?: getCurrentImgDataFunction | null }
    | getCurrentImgDataFunction,
): getCurrentImgDataFunction | null {
  if (typeof refOrFn === 'function') {
    return refOrFn as getCurrentImgDataFunction;
  }
  const maybeRef = refOrFn as { current?: getCurrentImgDataFunction | null };
  const candidate = maybeRef?.current ?? null;
  return typeof candidate === 'function' ? (candidate as getCurrentImgDataFunction) : null;
}

export function getBase64FromEditorImageData(
  imageData: EditorImageData | null | undefined,
  extension: string,
  quality = 0.92,
): string {
  let result = '';

  if (imageData?.imageBase64) {
    result = imageData.imageBase64 as string;
  } else if (imageData?.imageCanvas instanceof HTMLCanvasElement) {
    result = imageData.imageCanvas.toDataURL(
      `image/${extension}`,
      imageData?.quality ?? quality,
    );
  }

  return result;
}

export async function getFileLocalUrlFromEditorRef(
  getImgDataRef: GetImgDataRef,
  fileName: string,
  extension: string,
  quality = 0.92,
): Promise<FileLocalUrl | null> {
  let result: FileLocalUrl | null = null;

  try {
    const getFn = resolveEditorGetFn(getImgDataRef);
    if (!getFn) return null;

    const { imageData } = getFn(
      { name: fileName, extension, quality },
      false,
      false,
    );

    const base64 = getBase64FromEditorImageData(
      imageData,
      extension,
      quality,
    );

    if (base64) {
      const file = dataUrlToFile(base64, fileName);
      result = { dataUrl: base64, file, name: file.name };
    }
  } catch {
    result = null;
  }

  return result;
}
